import { _ } from "../../../polyglot/polyglot.js";
import { formatAsMonthAndYear } from "../../../helpers/date-formatting-util.js";
import { makeMonitorRepository } from "../../../repositories/repositoryBuilders.js";
import { router } from "@foretagsplatsen/widgetjs";
import AbstractImporter from "../AbstractImporter.js";
import AuthenticationStep from "../AuthenticationStep.js";
import DropdownSelectionStep from "../DropdownSelectionStep.js";
import session from "../../../session.js";

/**
 * `AbstractFortnoxImporter` base import class for the Fortnox integration.
 */
export default class AbstractFortnoxImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		const antiForgeryToken = session.getAntiForgeryToken();

		this._fortnoxRepository = makeMonitorRepository({
			route: "/Shared/Import/Fortnox",
			antiForgeryToken,
		});

		this._router = router.getRouter();

		this._companyChartOfAccountsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/ChartOfAccounts",
			antiForgeryToken,
		});

		this._budgetRepository = makeMonitorRepository({
			route: "/Accounting2/Company/#companyId/Budget/",
			antiForgeryToken,
		});
	}

	useFortnoxExtended() {
		throw new Error("Subclass responsibility");
	}

	routeName() {
		throw new Error("Subclass responsibility");
	}

	hasIntegration() {
		throw new Error("Subclass responsibility");
	}

	usesLegacyIntegration() {
		return this.hasIntegration() && !this._useOAuth2;
	}

	_wizardSteps() {
		return [
			this._createAuthenticationStep(),
			this._createFiscalYearSelectionStep(),
		];
	}

	// Step 1: Launch the authentication flow.
	_createAuthenticationStep() {
		return new AuthenticationStep({
			submitCallback: this._launchAuthenticationFlow.bind(this),
		});
	}

	// Step 2: Ask the user to select which fiscalYear to import.
	_createFiscalYearSelectionStep() {
		return new DropdownSelectionStep({
			dropdownLabel: _("Fiscal year"),
			getItemsFromContext: (context) =>
				context.fiscalYears.map((year) => ({
					label: `${formatAsMonthAndYear(
						year.start,
					)} - ${formatAsMonthAndYear(year.end)}`,
					value: year,
				})),
			submitCallback: this._fiscalYearToImportData.bind(this),
			name: "fortnox-fiscalyears",
			canGoToPreviousStep: false,
		});
	}

	// Step 1 => 2: Authenticate the user if needed or proceed with fetching
	// the fiscal years.
	_launchAuthenticationFlow({ onDone, step }) {
		const fortnoxIntegration = this.useFortnoxExtended()
			? this._context.company.getFortnoxExtendedIntegration()
			: this._context.company.getFortnoxIntegration();

		// Use OAuth2 if there is not an existing integration
		this._useOAuth2 =
			!fortnoxIntegration || fortnoxIntegration.isOAuth2Enabled();

		this._agencyId =
			this._context.agency?.getId() ||
			this._context.company?.getAgencyId();

		this._companyId =
			this._router.getParameter("companyId") ||
			this._context.company?._id;

		const hasAccessToken =
			this._router.getParameter("hasAccessToken") === "true";

		if (this.usesLegacyIntegration() || hasAccessToken) {
			// User is authenticated, proceed with the flow.
			this._authenticationToFiscalYears({ onDone, step });
		} else {
			// New integration, redirect to Fortnox for authentication.
			this._redirectToFortnox();
		}
	}

	// Step 1 => 2: Redirect to Fortnox in order to authenticate with OAuth2.
	_redirectToFortnox() {
		let query = `${
			window.location.pathname
		}#!/import/${this.routeName()}?hasAccessToken=true`;

		// Return and abort url need to be set.
		// Otherwise the default ones are lost after the redirection from Fortnox,
		// resulting in the Cancel and Done buttons redirecting to broken urls.
		query += `&abortUrl=${this._router.getParameter("abortUrl")}`;
		query += `&returnUrl=${this._router.getParameter("returnUrl")}`;
		if (this._companyId) {
			query += `&companyId=${this._companyId}`;
		}

		let redirectUrl = `/Shared/Import/FortnoxOAuth2?agencyId=${
			this._agencyId
		}&isFortnoxExtended=${this.useFortnoxExtended()}&returnUrl=${encodeURIComponent(
			query,
		)}&useOAuth2=${this._useOAuth2}`;
		if (this._companyId) {
			redirectUrl += `&companyId=${this._companyId}`;
		}

		this._router.redirectToLocationPath(redirectUrl);
	}

	// Step 1 ⇒ Step 2: get the list of available fiscal years
	// associated with the authentication.
	_authenticationToFiscalYears({ onDone, step }) {
		this._fortnoxRepository
			.get({
				filter: {
					agencyId: this._agencyId,
					useOAuth2: this._useOAuth2,
					companyId: this._companyId,
					useExtended: this.useFortnoxExtended(),
				},
				notify: false,
			})
			.then((fiscalYears) => {
				if (fiscalYears.length === 0) {
					step.addSubmitError(_("No years found to import."));
				} else {
					this._context.fiscalYears = fiscalYears;
				}
				onDone();
			})
			.catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});
	}

	// Step 2 ⇒ Step 3: get all the data corresponding to the selected
	// fiscal year (e.g., budgets, dimensions, dates, …)
	async _fiscalYearToImportData({ onDone, step, selection }) {
		const [importData, companyChartsOfAccounts, budgets] =
			await Promise.all([
				this._fetchFortnoxData(selection),
				this._fetchChartsOfAccounts(),
				this._fetchBudgets(),
			]).catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});

		const allChartsOfAccounts = new Set([
			...(importData.chartOfAccounts || []),
			...companyChartsOfAccounts,
		]);

		const allBudgets = new Set([...(importData.budgets || []), ...budgets]);

		importData.chartOfAccounts = [...allChartsOfAccounts];
		importData.budgets = [...allBudgets];

		this._context.importData = importData;
		onDone();
	}

	_fetchFortnoxData(financialYear) {
		return this._fortnoxRepository.save({
			filter: {
				agencyId: this._agencyId,
				companyId: this._companyId,
				useExtended: this.useFortnoxExtended(),
				financialYearId: financialYear.id,
				useOAuth2: this._useOAuth2,
			},
			notify: false,
		});
	}

	_fetchChartsOfAccounts() {
		return this._companyChartOfAccountsRepository.get({
			filter: { companyId: this._companyId },
		});
	}

	_fetchBudgets() {
		return this._budgetRepository.get({
			filter: { companyId: this._companyId },
		});
	}

	_wantsBudgetPicker() {
		return true;
	}

	_errorToMessage(error) {
		switch (error.responseJSON.typeOfError) {
			case "PreconditionFailed": {
				return _("Authorization code denied by Fortnox");
			}
			case "NoAccessToCurrentScope":
			case "NoActiveLicenseForScope": {
				return _(
					"Insufficient access privileges, please contact support.",
				);
			}
			case "FortnoxClientLicenseMissing": {
				return _(
					"The requested Fortnox account does not have a license to use the API.",
				);
			}
			case "UnhandledErrorCode": {
				return _(
					"An unexpected error occurred, please contact support.",
				);
			}
			case "DisabledAccessTokenOrInvalidClientSecret": {
				return _(
					"The access token is inactive, try to disconnect and import again.",
				);
			}
			case "InsufficientAccessPrivilegesToInvoices": {
				return _("You do not have permission to list invoices.");
			}
			default: {
				return error.responseJSON.message;
			}
		}
	}
}
